<script setup>
  /**
   * TransformQuizResultsBreakdown — read-only review of a COMPLETED quiz attempt.
   *
   * Reached in the same tab from the quiz result panel ("Results Breakdown") via the
   * virtual router, with { lessonId, lessonTitle, moduleId, moduleTitle } params
   * (sessionStorage-backed restore handles reloads, like the other Transform views).
   *
   * The answer key lives server-side; this view fetches the merged breakdown (questions
   * + correct answers + the user's own selections) from the gateway, which only reveals
   * the key once the attempt is completed.
   */
  import { ref, computed, inject, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'
  import Footer from '../FooterSection.vue'
  import Breadcrumbs from '../BreadCrumbs.vue'
  import BcsStatPill from '../common/BcsStatPill.vue'
  import BcstLessonBadge from './BcstLessonBadge.vue'
  import BcstQuizQuestionCard from './BcstQuizQuestionCard.vue'
  import TransformOwnerBanner from './TransformOwnerBanner.vue'
  import { TransformLearningProgressService } from '../../services/TransformLearningProgressService'
  import { useTransformOwnerContext } from '../../composables/useTransformOwnerContext'

  const customNavigation = inject('customNavigation')
  // Standard module loading pattern (same as TransformLessonDetail): the whole view is
  // gated behind !isLoading and the shared full-screen overlay covers the load, so the
  // user never sees a partial render (breadcrumb without content). Pushed in setup — the
  // earliest point — so it overlaps HomePage's boot loader with no gap, and released once
  // the loaded view has painted.
  const toggleLoadingWithMessage = inject('toggleLoadingWithMessage', () => {})
  toggleLoadingWithMessage(true, null)
  // Instantiated in setup: the service injects functionsBaseUrl, which requires an
  // active component instance.
  const progressService = TransformLearningProgressService()

  const lessonId = computed(() => customNavigation.value.navigationParams.lessonId ?? null)
  const lessonTitle = computed(() => customNavigation.value.navigationParams.lessonTitle ?? '')
  const moduleId = computed(() => customNavigation.value.navigationParams.moduleId ?? null)
  const moduleTitle = computed(() => customNavigation.value.navigationParams.moduleTitle ?? '')
  // Optional admin "view-as" flow: the module-wide owner context (seeded by navigation,
  // single source of truth) supplies the target user; authorization is enforced
  // server-side — this only forwards the id.
  const ownerContext = useTransformOwnerContext()
  const owner = computed(() => ownerContext.ownerUid.value)

  const isLoading = ref(true)
  const hasError = ref(false)
  const breakdown = ref(null)

  const questions = computed(() => breakdown.value?.questions ?? [])
  // Present only when a platform admin is viewing another user's results (backend gates this).
  const targetUser = computed(() => breakdown.value?.targetUser ?? null)
  const isViewingOtherUser = computed(() => !!targetUser.value)
  const targetUserName = computed(() => targetUser.value?.name?.trim() || 'another user')
  // Empty state: the request succeeded but the (resolved) user has no completed attempt to review.
  const noResults = computed(() => breakdown.value != null && breakdown.value.hasResults === false)
  const emptyStateText = computed(() =>
    isViewingOtherUser.value
      ? `${targetUserName.value} has not completed this quiz yet, so there are no results to show.`
      : 'You have not completed this quiz yet, so there are no results to show.'
  )
  const passed = computed(() => breakdown.value?.passed === true)
  const scoreLabel = computed(() =>
    breakdown.value?.scorePercent != null ? `${breakdown.value.scorePercent}%` : '—'
  )
  const passThresholdLabel = computed(() =>
    breakdown.value?.passThresholdPercent != null ? `${breakdown.value.passThresholdPercent}%` : '—'
  )
  const correctAnswersLabel = computed(() => {
    const correct = breakdown.value?.correctCount
    const total = breakdown.value?.totalCount
    return correct != null && total != null ? `${correct}/${total}` : '—'
  })
  // Authoritative lesson title from the backend response. The URL lessonTitle is only a
  // pre-fetch hint and can be stale (e.g. the lessonId is changed without updating the title),
  // so prefer the server value everywhere the title is shown — header, breadcrumb, back link.
  const resolvedLessonTitle = computed(() => breakdown.value?.quizTitle || lessonTitle.value || '')
  const headerTitle = computed(() => resolvedLessonTitle.value || 'Quiz')
  // Module is resolved server-side from the lesson (the lesson doc carries moduleId), so the
  // breakdown no longer depends on the URL passing moduleId/moduleTitle. Fall back to the URL
  // params only until the backend responds. resolvedModuleId also lets the module breadcrumb and
  // "Back to Lesson" navigate correctly even for deep links that never carried it (the email).
  const resolvedModuleId = computed(() => breakdown.value?.moduleId ?? moduleId.value ?? null)
  const resolvedModuleTitle = computed(() => breakdown.value?.moduleTitle || moduleTitle.value || '')

  const completedLabel = computed(() => {
    const iso = breakdown.value?.completedAt
    if (!iso) return null
    const d = new Date(iso)
    return Number.isNaN(d.getTime())
      ? null
      : d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
  })

  function goToLesson() {
    if (!lessonId.value) return
    customNavigation.value.navigate({
      newPage: customNavigation.value.routes.transformLessonDetail.name,
      params: {
        lessonId: lessonId.value,
        lessonTitle: resolvedLessonTitle.value,
        moduleId: resolvedModuleId.value,
        moduleTitle: resolvedModuleTitle.value,
      },
    })
  }

  // The route carries static 'Category' / 'Lesson' placeholders for those crumbs; patch
  // them to the real category + lesson names (from the query params we opened with),
  // leaving the current "Results Breakdown" crumb intact. Mirrors TransformLessonDetail.
  function patchBreadcrumbTitles() {
    const crumbs = customNavigation.value.breadCrumbs
    const moduleCrumb = crumbs.find(c => c.name === customNavigation.value.routes.transformModuleDetail.name)
    const lessonCrumb = crumbs.find(c => c.name === customNavigation.value.routes.transformLessonDetail.name)
    const lessonName = resolvedLessonTitle.value
    if (moduleCrumb && resolvedModuleTitle.value) moduleCrumb.title = resolvedModuleTitle.value
    if (lessonCrumb && lessonName) lessonCrumb.title = lessonName
  }

  // Re-patch whenever the breadcrumbs are rebuilt (back/forward, re-navigation).
  watch(() => customNavigation.value.breadCrumbs, patchBreadcrumbTitles, { deep: true })

  async function load() {
    isLoading.value = true
    hasError.value = false
    try {
      if (!lessonId.value) {
        hasError.value = true
        return
      }
      breakdown.value = await progressService.getQuizResultBreakdown(lessonId.value, owner.value)
      if (!breakdown.value) hasError.value = true
      // Feed the target's display name into the shared owner context so the module-wide
      // banner shows it (this endpoint is the resolver on a direct deep link, where no
      // dashboard progress fetch has run yet). No targetUser while an owner was requested
      // means the server fell back to the caller's own results (nonexistent user) — exit
      // the context so the banner never claims data that is actually the admin's own.
      if (breakdown.value?.targetUser) ownerContext.setTargetUser(breakdown.value.targetUser)
      else if (owner.value && breakdown.value) ownerContext.exit()
      // The backend is authoritative for the lesson/module names and the moduleId. Write them
      // into the shared nav params so downstream navigation works even when the deep link only
      // carried lessonId + owner (the email case never sent moduleId/moduleTitle): the module
      // breadcrumb and "Back to Lesson" both read moduleId from navigationParams at click time.
      const navParams = customNavigation.value.navigationParams
      if (breakdown.value?.quizTitle) navParams.lessonTitle = breakdown.value.quizTitle
      if (breakdown.value?.moduleId) navParams.moduleId = breakdown.value.moduleId
      if (breakdown.value?.moduleTitle) navParams.moduleTitle = breakdown.value.moduleTitle
      // quizTitle is the authoritative lesson name; patch again in case the query
      // param was missing.
      patchBreadcrumbTitles()
    } catch {
      hasError.value = true
    } finally {
      // Reveal the fully-loaded view, then release the overlay on the next tick so it
      // stays up until the content (or error state) has actually painted — no blank frame.
      isLoading.value = false
      await nextTick()
      toggleLoadingWithMessage(false, null)
    }
  }

  // Keep the browser URL in sync with the view's params so it is identical regardless of entry
  // point (the in-app "Results Breakdown" button or the email deep link) and is sufficient to
  // reconstruct the view on refresh. Only lessonId + owner are needed: the backend resolves the
  // lesson title, moduleId and module title from the lessonId, so the cosmetic params are no
  // longer carried (they could go stale if the lessonId changed without them). Uses
  // history.replaceState — not the router — to avoid a navigation cycle in the virtual router.
  function syncUrlQuery() {
    if (!lessonId.value) return
    const params = new URLSearchParams()
    params.set('view', customNavigation.value.routes.transformQuizResultsBreakdown.name)
    params.set('lessonId', lessonId.value)
    // Owner is always explicit: the admin target when viewing another user, otherwise the
    // authenticated user's own uid (backend resolves owner === self to own results, no banner).
    const ownerId = owner.value || localStorage.getItem('userUid')
    if (ownerId) params.set('owner', ownerId)
    window.history.replaceState(window.history.state, '', `${window.location.pathname}?${params.toString()}`)
  }

  onMounted(() => {
    syncUrlQuery()
    patchBreadcrumbTitles()
    load()
  })

  // Leave the URL clean on exit so a later view's refresh never restores this breakdown from a
  // stale query (the deep-link restore in HomePage keys off `view`/`lessonId`).
  onBeforeUnmount(() => {
    window.history.replaceState(window.history.state, '', window.location.pathname)
  })
</script>

<template>
  <!-- Whole view gated behind !isLoading: while loading, the global overlay covers and
       nothing here renders, so the user never sees a partial (breadcrumb-only) state. -->
  <div v-if="!isLoading" class="transform-results-breakdown">
    <!-- Admin context: viewing another user's results. Shared module-wide banner with
         this view's specific wording; full-width and sticky, like the other views.
         The trb-admin-banner class is kept as the QA selector. -->
    <TransformOwnerBanner class="trb-admin-banner">
      <template #default="{ targetUserName: bannerName }">
        You are viewing the quiz results for {{ bannerName }}.
      </template>
    </TransformOwnerBanner>
    <div class="trb-main">
      <Breadcrumbs />

      <!-- Back to the lesson (quiz) -->
      <p class="trb-back-link">
        <span
          class="trb-back-trigger"
          role="button"
          tabindex="0"
          @click="goToLesson"
          @keydown.enter.prevent="goToLesson"
          @keydown.space.prevent="goToLesson">
          &lt; Back to Lesson: {{ resolvedLessonTitle }}
        </span>
      </p>

      <!-- Header -->
      <div class="trb-title-row">
        <h1 class="trb-title">Results Breakdown: {{ headerTitle }}</h1>
        <BcstLessonBadge v-if="!hasError && !noResults" :variant="passed ? 'success' : 'danger'">
          {{ passed ? 'Passed' : 'Not Passed' }}
        </BcstLessonBadge>
      </div>
      <p v-if="resolvedModuleTitle || completedLabel" class="trb-subtitle">
        <span v-if="resolvedModuleTitle">{{ resolvedModuleTitle }}</span>
        <span v-if="resolvedModuleTitle && completedLabel" class="trb-subtitle-sep">·</span>
        <span v-if="completedLabel">Completed {{ completedLabel }}</span>
      </p>

      <!-- ── Error / not found ── -->
      <div v-if="hasError" class="trb-state trb-state--error">
        <i class="bi bi-exclamation-triangle trb-state-icon" aria-hidden="true"></i>
        <p class="trb-state-text">We couldn't load this quiz result. It may be unavailable or not yet completed.</p>
        <button type="button" class="btn btn-outline-primary" @click="load">Try again</button>
      </div>

      <!-- ── No results yet (empty state — not an error) ── -->
      <div v-else-if="noResults" class="trb-state trb-state--empty">
        <i class="bi bi-clipboard-x trb-state-icon" aria-hidden="true"></i>
        <p class="trb-state-text">{{ emptyStateText }}</p>
      </div>

      <template v-else>
        <!-- ── Quiz summary ── -->
        <section class="trb-summary" aria-label="Quiz summary">
          <BcsStatPill :value="scoreLabel" label="Your Score" size="md" />
          <BcsStatPill :value="passThresholdLabel" label="Passing Score" size="md" />
          <BcsStatPill :value="correctAnswersLabel" label="Correct Answers" size="md" />
        </section>

        <!-- ── Question-by-question review — reuses the lesson-detail quiz card in
             read-only review mode so the look matches the quiz player exactly. ── -->
        <section class="trb-review" aria-label="Question review">
          <BcstQuizQuestionCard
            v-for="(question, qi) in questions"
            :key="question.id"
            :question="question"
            :questionIndex="qi"
            :totalQuestions="questions.length"
            :selected="question.selectedOptionIds"
            :correctOptionIds="question.correctOptionIds"
            reviewMode />
        </section>
      </template>
    </div>

    <Footer :showMarginTop="false" />
  </div>
</template>

<style scoped lang="scss">
  .transform-results-breakdown {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  .transform-results-breakdown :deep(.breadcrumbs) {
    margin-bottom: 32px !important;
  }

  .trb-main {
    flex: 1 1 auto;
    min-width: 0;
    /* Mirrors the lesson-detail gutter. */
    padding: 24px 9% 48px;
  }

  /* ── Back link ── */
  .trb-back-link {
    margin: 0 0 16px;
  }

  .trb-back-trigger {
    color: var(--bs-primary);
    font-weight: 700;
    font-size: 14px;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }

    &:focus-visible {
      outline: 2px solid var(--bs-primary);
      outline-offset: 2px;
      border-radius: 2px;
    }
  }

  /* ── Header ── */
  .trb-title-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
  }

  .trb-title {
    color: var(--Body-Color, #212529);
    font-family: 'DM Sans', sans-serif;
    font-size: 28px;
    font-weight: 500;
    line-height: 130%;
    letter-spacing: -0.56px;
    margin: 0;
  }

  .trb-subtitle {
    color: var(--Gray-700, #495057);
    font-size: 16px;
    line-height: 130%;
    margin: 8px 0 0;
  }

  .trb-subtitle-sep {
    margin: 0 8px;
  }

  /* ── Loading / error states ── */
  .trb-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    width: 100%;
    min-height: 320px;
    margin-top: 32px;
    border: 1px dashed var(--bs-gray-400);
    border-radius: 12px;
    background-color: var(--bs-gray-100, #f8f9fa);
    padding: 48px 24px;
    text-align: center;
  }

  .trb-state-icon {
    font-size: 2.5rem;
    color: var(--bs-danger);
  }

  /* Empty state is informational, not an error — neutral icon color. */
  .trb-state--empty .trb-state-icon {
    color: var(--bs-gray-500, #adb5bd);
  }

  .trb-state-text {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--bs-body-color);
    max-width: 520px;
  }

  /* ── Summary ── */
  .trb-summary {
    display: flex;
    flex-wrap: wrap;
    gap: 48px;
    margin: 32px 0 40px;
  }

  /* ── Question review ── */
  .trb-review {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  @media (max-width: 991.98px) {
    .trb-main {
      padding: 24px 6% 32px;
    }
  }
</style>
